/* eslint-disable import/order */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types';
import { Box, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useParams } from 'react-router-dom';

// Utils
import { AuthContext } from 'authentication/AuthContext';
import * as R from 'ramda';
import { useSetNoAuthCard, useSetSelectorIndex, useURLRouting } from 'hooks';
import { isAuthSequencingUpdatesEnabled, sidebarStyles, VIEW_BY_OPTIONS } from '../../utils/const';

// Components
import Selector from './Selector.connector';
import SelectorSkeleton from './SelectorsSkeleton';
import SelectorsList from '../../selectors-list/SelectorsList';
import SelectorVirtualizer from '../../selectors-list/SelectorVirtualizer';
import Filters from '../../filters/Filters.connector';
import MobileFilters from '../../filters/MobileFilters';
import { NoAuthorizationsAvailable } from 'feature/loading-management/shared-components';

const SelectorsListStyled = styled(SelectorsList)(({ theme }) => ({
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  height: '100%',
  overflowY: 'scroll',
  padding: theme.spacing(0, 0, 4.5),
  scrollbarWidth: 'none',
}));

const Sidebar = props => {
  const SELECTOR_SKELETON_HEIGHT = 100;
  const {
    authorizationSequencingPending,
    currentSelection,
    fetchAuthorizationSequencingCards,
    fetchUniqueSelectors,
    relatedIds,
    resetAuthSequencing,
    resetCardFilters,
    selectorsListPending,
    selectorsList,
    selectorsListRaw,
    setAuthorizationSequencingPending,
    setFilters,
    setFiltersOpen,
  } = props;
  const { currentAffiliate } = useContext(AuthContext);
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const selectorItemSize = 100;
  const selectorItemSizeWithChips = 84;
  const selectorItemSizeWithoutChips = 56;

  // Feature flag: REACT_APP_LD_SEQUENCING_AUTH_LIST
  const isAuthSequencingUpdatesFlagOn = isAuthSequencingUpdatesEnabled();

  const previousSelectorFetch = useRef();

  const { viewBy, viewById } = useParams();
  const adjustedViewById = R.o(R.replace(/%/g, '%25'), R.replace(/%23/g, '#'))(viewById || '');
  const isEnhancedRelatedFilterMode =
    isAuthSequencingUpdatesFlagOn && (viewBy === 'location' || viewBy === 'product');

  const [refetch, setRefetch] = useState(false);

  useSetSelectorIndex(selectorsListRaw, setFilters);

  // this will updated the currentSelection when the user hits the back button
  useURLRouting(currentSelection, selectorsListRaw, adjustedViewById, setFilters);

  useSetNoAuthCard(selectorsList, selectorsListPending, setAuthorizationSequencingPending, refetch);

  const getChipFields = React.useCallback(() => {
    if (!isAuthSequencingUpdatesFlagOn) {
      return ['baseProductCount', 'customProductCount', 'locationCount'];
    }

    return viewBy === 'product' ? ['baseProductCount', 'customProductCount'] : ['locationCount'];
  }, [isAuthSequencingUpdatesFlagOn, viewBy]);

  const hasVisibleChips = React.useCallback(
    selector => getChipFields().some(field => Boolean(selector && selector[field])),
    [getChipFields]
  );

  const getSelectorItemSize = React.useCallback(
    index => {
      if (!isAuthSequencingUpdatesFlagOn) {
        return selectorItemSize;
      }

      return hasVisibleChips(selectorsList[index])
        ? selectorItemSizeWithChips
        : selectorItemSizeWithoutChips;
    },
    [isAuthSequencingUpdatesFlagOn, selectorsList, hasVisibleChips]
  );

  const buildCardFetchParams = (id, companyCustomerIdName, dataCompanyId) => {
    const normalizedIds = relatedIds
      .map(value => Number(value))
      .filter(value => !Number.isNaN(value));
    const relatedIdsKey = viewBy === 'location' ? 'productIds' : 'locationIds';
    const shouldIncludeRelatedIds = isEnhancedRelatedFilterMode && relatedIds.length > 0;

    return {
      supplier: { companyCustomerIdName, dataCompanyId },
      [`${viewBy}Id`]: id,
      ...(shouldIncludeRelatedIds && {
        [relatedIdsKey]: normalizedIds.length ? normalizedIds : relatedIds,
      }),
    };
  };

  const fetchCardsForSelection = (id, companyCustomerIdName, dataCompanyId) =>
    fetchAuthorizationSequencingCards(
      buildCardFetchParams(id, companyCustomerIdName, dataCompanyId),
      viewBy,
      undefined,
      isAuthSequencingUpdatesFlagOn
    );

  useEffect(() => {
    // wait till we have an affiliate to fetch data
    if (currentAffiliate?.customerNameShort) {
      previousSelectorFetch.current = null;
      const { dataCompanyId, customerIdName: companyCustomerIdName } = currentAffiliate;
      fetchUniqueSelectors(
        {
          supplier: { companyCustomerIdName, dataCompanyId },
        },
        viewBy
      )
        // Fetch is a promise if we get some data back
        // set the refetch to false so that the cards
        // panel will render correctly
        .then(res => res?.length && setRefetch(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAffiliate?.customerNameShort, currentAffiliate?.dataCompanyId, viewBy]);

  useEffect(() => {
    const id = currentSelection.locationId || currentSelection.productId;
    if (id && previousSelectorFetch.current !== currentSelection.nameShort) {
      previousSelectorFetch.current = currentSelection.nameShort;
      const { dataCompanyId, customerIdName: companyCustomerIdName } = currentAffiliate;
      fetchCardsForSelection(id, companyCustomerIdName, dataCompanyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelection.locationId, currentSelection.productId, fetchAuthorizationSequencingCards]);

  useEffect(() => {
    const id = currentSelection.locationId || currentSelection.productId;
    if (!id || !currentAffiliate?.customerNameShort) return;
    if (!isEnhancedRelatedFilterMode) return;

    const { dataCompanyId, customerIdName: companyCustomerIdName } = currentAffiliate;
    fetchCardsForSelection(id, companyCustomerIdName, dataCompanyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnhancedRelatedFilterMode, relatedIds]);

  // cleanup function so user does not return to stale or inaccuracte data
  useEffect(() => {
    return () => {
      resetAuthSequencing();
      setFiltersOpen(false);
      resetCardFilters();
    };
  }, [resetAuthSequencing, resetCardFilters, setFiltersOpen]);

  useEffect(() => {
    authorizationSequencingPending && resetCardFilters();
  }, [authorizationSequencingPending, resetCardFilters]);

  return (
    <Box {...sidebarStyles}>
      {!lgUp ? (
        <MobileFilters>
          <Filters loading={selectorsListPending} viewByOptions={VIEW_BY_OPTIONS} />
        </MobileFilters>
      ) : (
        <Filters loading={selectorsListPending} viewByOptions={VIEW_BY_OPTIONS} />
      )}
      {!lgUp && !selectorsListPending && R.isEmpty(selectorsList) ? (
        <Box p={3}>
          <NoAuthorizationsAvailable />
        </Box>
      ) : (
        <SelectorsListStyled
          loading={selectorsListPending}
          selector={
            <SelectorVirtualizer
              itemSize={getSelectorItemSize}
              // Adding a key to force remount the virtualizer when the
              // flag changes to avoid bugs related to changing item sizes
              key={isAuthSequencingUpdatesFlagOn ? 'with-flag' : 'without-flag'}
              overscanCount={10}
              selectorComponent={Selector}
              selectorsData={selectorsList}
            />
          }
          skeletonComponent={<SelectorSkeleton skeletonHeight={SELECTOR_SKELETON_HEIGHT} />}
          skeletonHeight={SELECTOR_SKELETON_HEIGHT}
        />
      )}
    </Box>
  );
};

Sidebar.propTypes = {
  authorizationSequencingPending: bool.isRequired,
  currentSelection: shape({}).isRequired,
  fetchAuthorizationSequencingCards: func.isRequired,
  fetchUniqueSelectors: func.isRequired,
  relatedIds: arrayOf(oneOfType([string, number])),
  resetAuthSequencing: func.isRequired,
  resetCardFilters: func.isRequired,
  selectorsList: arrayOf(
    shape({
      locationCount: number,
      nameShort: string,
      productCount: number,
      proprietary: bool,
    })
  ),
  selectorsListPending: bool.isRequired,
  selectorsListRaw: arrayOf(shape({})),
  setAuthorizationSequencingPending: func.isRequired,
  setFilters: func.isRequired,
  setFiltersOpen: func.isRequired,
};

Sidebar.defaultProps = {
  relatedIds: [],
  selectorsList: [],
  selectorsListRaw: [],
};

export default Sidebar;
